import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PlusOutlined,
  DeleteOutlined,
  DownOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { ConfigProvider, Spin, Tooltip } from "antd";
import type { FormInstance, FormListFieldData } from "antd";
import {
  Form,
  Input,
  Select,
  Button,
  Collapse,
  StatusBadge,
  ToggleSwitch,
} from "../../../../shared";
import { filterByLabel } from "../../../../../utils/selectFilter";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { formatString } from "../../../../../utils/constants/stringUtils";
import { usePamFeature } from "../../../../../hooks/useFeatureFlag";
import { useAffiliatesList } from "../../../../../queries/affiliates/useAffiliatesList";
import { useAffiliatePromoCodes } from "../../../../../queries/affiliates/useAffiliatePromoCodes";
import ConditionsField from "../../controls/ConditionsField";
import DisplayLocationsField from "../../controls/DisplayLocationsField";
import FreeSpinProviderSelect from "../../controls/FreeSpinProviderSelect";
import FreeSpinGameSelect from "../../controls/FreeSpinGameSelect";
import ClientCurrencyField from "../../controls/ClientCurrencyField";
import {
  FutureBonusExpiryPicker,
  FutureBonusLeagueSelect,
  FutureBonusSettlementDisplay,
  FutureBonusSportSelect,
  FutureBonusTraitSelect,
} from "../../controls/FutureBonusFields";
import StepBannerEditor from "../../controls/StepBannerEditor";
import FreeformBannerEditor from "../../controls/FreeformBannerEditor";
import {
  RESTRICTION_DISPLAY_NAMES_PARAM_KEY,
  RestrictionDisplayNamesControl,
  clearRestrictionDisplayNames,
} from "../../controls/restrictionDisplayNames";
import { initializeBannerThemePreset } from "../../controls/bannerThemes";
import {
  DEFAULT_TRANSACTION_TITLES,
  PERCENT_OF_TRIGGER_EVENT_KEYS,
  PERCENT_OF_TRIGGER_PARAM_KEY,
  PRIMARY_CASH_WALLET,
  REFUND_ON_LOSS_RESULT_TYPE_KEY,
  REFUND_ON_LOSS_WALLET_PARAM_KEY,
  RESULT_PARAM_SCHEMA,
  isLegacyRefundPromoWallet,
  resultParamsForAuthoring,
  supportsStepBannerEditor,
  TRANSACTION_TITLE_PARAM_KEY,
} from "../../constants";
import { resultOptionLabel } from "../../bonusTypeCatalog";
import { usePrimaryCurrencyName } from "../../usePrimaryCurrencyName";
import { conditionDisplayLabel } from "../../conditionTypeCatalog";
import { paramFieldRules, conditionalParamDependencyKeys } from "../../resultParamRules";
import type { PositiveAmountContext } from "../../resultParamRules";
import { displayNameFor, eventForKey, fieldsForEvent, toGroupedSelectOptions } from "../../eventCatalog";
import ShowBonusTargetingField from "../../controls/ShowBonusTargetingField";
import {
  assembleRewardSizingGrid,
  useRewardSizingAuthoring,
  type RewardSizingMode,
} from "../../controls/rewardSizing";
import {
  CASHBACK_PERIOD_EVENT_KEYS,
  cashbackPeriodForEventKey,
  eventKeyForCashbackPeriod,
  isCashbackCalculationPeriod,
  isCashbackPeriodEvent,
  isCashbackResult,
} from "../../cashbackAuthoring";
import {
  APPLIED_OFFER_MATCHES_TYPE,
  CAMPAIGN_START_EVENT_KEY,
  DEFAULT_CAMPAIGN_TYPE,
  DEFAULT_DISPLAY_BANNER_CLAIM,
  DEFAULT_FREEFORM_BANNER_LAYOUT,
  DISPLAY_BANNER_CAMPAIGN_TYPE,
  DISPLAY_BANNER_RESULT_TYPE_KEY,
  SERIES_CAMPAIGN_TYPE,
  appliedOfferToggleAvailable,
  hasCampaignStartEvent,
  injectedFollowUpConditionsFor,
  injectedStepConditionsFor,
  isWizardBetTargetedPreset,
  stepsForBetTargetedPreset,
  wizardInitialValuesForCampaignType,
} from "./toWizardCampaignIn";
import type {
  WizardBetTargetedPreset,
  WizardCampaignType,
  WizardFormValues,
  WizardStepFormValue,
  WizardTriggerMode,
  FreeformBannerLayout,
} from "./toWizardCampaignIn";
import {
  defaultDisplayLocations,
  displayLocationsRequiredRule,
} from "./displayLocationDefaults";
import { formFieldTooltip } from "./formFieldTooltip";
import {
  isInformResultType,
  type BannerIn,
  type ConditionSpecIn,
  type DynamicBonusCatalog,
  type EventCategoryGroup,
} from "../../types";
import styles from "../../stylesDynamicBonuses.module.css";

const TRIGGER_MODES = (): Array<{ mode: WizardTriggerMode; label: string }> => [
  {
    mode: "campaign_start",
    label: AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_BASIC,
  },
  { mode: "event", label: AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_EVENT },
  {
    mode: "referral",
    label: AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_FRIEND_REFERRAL,
  },
  {
    mode: "affiliate_referral",
    label: AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_AFFILIATE_REFERRAL,
  },
];

/** BONENG-1130: User Referral + Affiliate Referral stay hidden while beta mode is off. */
const BETA_HIDDEN_TRIGGER_MODES = new Set<WizardTriggerMode>([
  "referral",
  "affiliate_referral",
]);

/** Registers an object-valued form field without rendering a control. */
const FormObjectAnchor: React.FC<{
  value?: FreeformBannerLayout;
  onChange?: (value: FreeformBannerLayout) => void;
}> = () => null;

/**
 * Result types allowed on step 1 of a `campaign_start` campaign: the
 * `show_bonus` OFFER, or any AWARD result except period-end cashback
 * (BONENG-432 — that result needs `cashback_period_end_*`). An award on
 * campaign start completes the bonus in one step, with the step banner
 * doubling as the award's receipt (BONENG-849). `send_notification` is
 * hidden from the Result picker entirely (BONENG-882); schema-less AWARD
 * types are excluded by `isAuthorable`. `credit_wallet` is authorable via
 * RESULT_PARAM_SCHEMA (BONENG-788).
 *
 * Also reused for a Series' step 1 (BONENG-1023): the backend accepts the
 * same single-step campaign_start AWARD shape for a series (completing the
 * whole series in one step, no follow-up event), alongside the two-step
 * OFFER + AWARD blueprint. Display Banner is allowed on step 1 when the
 * v2 Result path is on (BONENG-1189).
 */
const isCampaignStartStepOneResult = (result: { type_key: string; result_type: string }): boolean =>
  result.type_key === "show_bonus" ||
  result.type_key === DISPLAY_BANNER_RESULT_TYPE_KEY ||
  (result.result_type === "award" && !isCashbackResult(result.type_key));

/**
 * Only offer catalog results the FE actually knows how to author. OFFER
 * results are always authorable — banner copy lives in `steps[].banner`
 * (`StepBannerEditor`); optional result params from `RESULT_PARAM_SCHEMA`
 * are authored as chips under the banner for every result type. AWARD
 * results need a `RESULT_PARAM_SCHEMA` entry to be authorable — picking a
 * schema-less type_key (retired `create_deposit_match_promotion`, BONENG-846)
 * would silently submit `params: {}` and fail upstream with a generic toast.
 * Supersedes the old single-purpose `type_key !== "credit_wallet"` check —
 * `credit_wallet` is authorable via RESULT_PARAM_SCHEMA (BONENG-788).
 * `send_notification` keeps catalog presence for legacy payloads but is
 * hidden from the Result picker (BONENG-882); `award_bonus_bets` is likewise
 * hidden and additionally has no schema entry — it was retired and
 * consolidated into Free Picks via `split_count` (BONENG-883).
 * `award_free_cash` is hidden from *new* authoring (BONENG-1237) — it is a
 * wallet credit (`increment_balance`), not a Free Pick grant. Existing
 * campaigns still hydrate via RESULT_PARAM_SCHEMA, and the Result picker
 * keeps the current value listed so an edit cannot lose the type.
 * `free_cash_award` is a PAM catalog leftover, not a backend result; hide
 * it if a catalog still returns it. `award_cashback` is gated by
 * `bonus.cashback_offers` and event-trigger mode (BONENG-432).
 * `award_future_bonus` is gated by `bonus.future_bonuses_pam` (BONENG-441).
 * Display Banner (`info`) is gated by `bonus.display_banner_v2`
 * (BONENG-1189) — it has no RESULT_PARAM_SCHEMA and is not an OFFER.
 */
const HIDDEN_RESULT_TYPE_KEYS = new Set([
  "award_bonus_bets",
  "send_notification",
  "award_free_cash",
  "free_cash_award",
]);

type AuthorableOptions = {
  cashbackOffersEnabled?: boolean;
  triggerMode?: WizardTriggerMode;
  futureBonusesEnabled?: boolean;
  displayBannerV2Enabled?: boolean;
};

const isAuthorable = (
  result: { type_key: string; result_type: string },
  options: AuthorableOptions = {},
): boolean => {
  if (HIDDEN_RESULT_TYPE_KEYS.has(result.type_key)) return false;
  if (result.type_key === DISPLAY_BANNER_RESULT_TYPE_KEY) {
    return options.displayBannerV2Enabled === true;
  }
  if (result.type_key === "award_future_bonus" && !options.futureBonusesEnabled) {
    return false;
  }
  if (isCashbackResult(result.type_key)) {
    if (!options.cashbackOffersEnabled) return false;
    // Period-end cashback is not a campaign_start / referral / series award.
    if (options.triggerMode && options.triggerMode !== "event") return false;
  }
  return (
    result.result_type === "offer" ||
    Boolean(RESULT_PARAM_SCHEMA[result.type_key]?.length)
  );
};

const cashbackEventSelectOptions = (
  groups: EventCategoryGroup[],
): ReturnType<typeof toGroupedSelectOptions> => {
  const filtered = groups
    .map((group) => ({
      ...group,
      events: group.events.filter((e) => isCashbackPeriodEvent(e.event_key)),
    }))
    .filter((group) => group.events.length > 0);
  if (filtered.length > 0) return toGroupedSelectOptions(filtered);
  return [
    {
      label: AppStrings.PAM_DYNAMIC_BONUS_TYPE_CATEGORY_GENERAL,
      options: [
        {
          label: AppStrings.PAM_DYNAMIC_BONUS_CASHBACK_EVENT_WEEKLY,
          value: CASHBACK_PERIOD_EVENT_KEYS.weekly,
        },
        {
          label: AppStrings.PAM_DYNAMIC_BONUS_CASHBACK_EVENT_MONTHLY,
          value: CASHBACK_PERIOD_EVENT_KEYS.monthly,
        },
      ],
    },
  ];
};

/** Human-readable label for the UI. The backend still receives `step_N`. */
const stepDisplayLabel = (index: number): string => `Step ${index + 1}`;

/** Placement events that also fire for bonus-funded (free pick) stakes. */
const BET_PLACEMENT_EVENT_KEYS = new Set([
  "bet_placed",
  "placed_live_bet",
  "places_bet_rebet_cash",
  "places_profit_boost_bet",
]);

/**
 * Live per-unit preview for a split Free Picks grant (Bet and Get,
 * BONENG-883). Mirrors the backend's ROUND_DOWN cent math so the operator
 * can check the exact unit value that will be issued. No Split Count means a
 * single grant — nothing to preview. A split of 1 is NOT the same as blank
 * (it issues one unit-valued Bet-and-Get pick, a different artifact than a
 * regular free pick), so that case is a "warning" (always visible) while the
 * per-unit math is a "preview" (informational — surfaced via a hover hint so
 * it doesn't compete with the warning for attention).
 */
type SplitConfirmation = { kind: "warning" | "preview"; text: string };

const splitConfirmationInfo = (
  params?: Record<string, unknown>,
): SplitConfirmation | null => {
  const splitCount = Number(params?.split_count);
  // The split=1 callout must not depend on Amount being filled in yet — the
  // operator needs the warning as soon as they type the 1.
  if (splitCount === 1) {
    return {
      kind: "warning",
      text: "Split Count 1 issues a single Bet-and-Get style pick — leave the field blank for a regular free pick.",
    };
  }
  const total = Number(params?.amount);
  // Non-integer splits (e.g. 1.5) get a field error from paramFieldRules;
  // showing a per-unit preview next to it would be contradictory.
  if (!Number.isFinite(total) || !Number.isInteger(splitCount) || splitCount <= 0) {
    return null;
  }
  const perUnit = Math.floor((total / splitCount) * 100) / 100;
  if (perUnit <= 0) {
    return null;
  }
  return { kind: "preview", text: `Per-unit value: $${perUnit.toFixed(2)} per free pick` };
};

const isSplitCountSet = (params?: Record<string, unknown>): boolean => {
  if (params?.split_count === undefined || params?.split_count === null || params?.split_count === "") {
    return false;
  }
  const splitCount = Number(params.split_count);
  return Number.isFinite(splitCount) && splitCount > 0;
};

const hasMoneyTypeEqualsCondition = (
  conditions?: { type: string; params?: Record<string, unknown> }[],
): boolean =>
  (conditions ?? []).some(
    (condition) =>
      condition.type === "event_field_equals" && condition.params?.field === "money_type",
  );

const hasAppliedOfferMatchesCondition = (
  conditions?: { type: string; params?: Record<string, unknown> }[],
): boolean => (conditions ?? []).some((condition) => condition.type === "applied_offer_matches");

/**
 * Non-blocking authoring guardrail for split Free Picks on bet-placement
 * triggers (BONENG-896). Recommends gating to cash stakes; for follow-up
 * steps eligible for the "Step 1 Promo Applied" toggle (BONENG-962), also
 * recommends turning that toggle back on when the operator disabled it.
 */
const splitAuthoringGuidanceText = ({
  resultTypeKey,
  resultParams,
  eventKey,
  conditions,
  steps,
  isFirstStep,
}: {
  resultTypeKey: string;
  resultParams?: Record<string, unknown>;
  eventKey: string;
  conditions?: { type: string; params?: Record<string, unknown> }[];
  steps?: WizardStepFormValue[];
  isFirstStep: boolean;
}): string | null => {
  if (resultTypeKey !== "award_free_picks") return null;
  if (!isSplitCountSet(resultParams)) return null;
  if (!BET_PLACEMENT_EVENT_KEYS.has(eventKey)) return null;

  const needsMoneyType = !hasMoneyTypeEqualsCondition(conditions);
  // The applied-offer nudge only makes sense where the toggle exists — a
  // follow-up step whose step 1 result and own trigger event qualify.
  const needsAppliedOffer =
    !isFirstStep &&
    appliedOfferToggleAvailable(steps?.[0]?.result_type_key, eventKey) &&
    !hasAppliedOfferMatchesCondition(conditions);
  if (!needsMoneyType && !needsAppliedOffer) return null;

  const parts: string[] = [];
  if (needsMoneyType) parts.push(AppStrings.DYNAMIC_BONUS_SPLIT_MONEY_TYPE_HINT);
  if (needsAppliedOffer) parts.push(AppStrings.DYNAMIC_BONUS_SPLIT_APPLIED_OFFER_HINT);
  return parts.join(" ");
};

type Props = {
  form: FormInstance<WizardFormValues>;
  catalog: DynamicBonusCatalog | undefined;
  eventGroups: EventCategoryGroup[];
  /** Resolved event_key for step 1 when a campaign-level trigger is set. */
  stepOneEventKey: string;
  onNext?: () => void;
  onPrev?: () => void;
  /** When false, omit the step footer (combined Details screen owns Next). */
  showFooter?: boolean;
  /** Active campaigns keep rules disabled while banner fields stay editable. */
  rulesLocked?: boolean;
  /** View-only host (series child). Dims Display Locations like Priority. */
  readOnly?: boolean;
};

type StepFieldsProps = {
  field: FormListFieldData;
  isFirst: boolean;
  form: FormInstance<WizardFormValues>;
  catalog: DynamicBonusCatalog | undefined;
  eventGroups: EventCategoryGroup[];
  eventSelectOptions: ReturnType<typeof toGroupedSelectOptions>;
  stepOneEventKey: string;
  usesFixedStepOneTrigger: boolean;
  isCampaignStartTrigger: boolean;
  /**
   * When true (affiliate_referral step 1), only auto-injected referral /
   * affiliate conditions are authorable — hide Add / free-form picker
   * (BONENG-959).
   */
  lockConditionsToInjected: boolean;
  /** Current step count — only used to validate the single-step guard below. */
  stepsCount: number;
  /** Series (BONENG-842) lock every non-first step's Result picker to AWARD-only. */
  isSeries: boolean;
  /**
   * When true, skip series Result locks so a hydrated template keeps
   * the full authorable result list.
   */
  unlockResultOptions: boolean;
  injectedConditions: ConditionSpecIn[];
  /**
   * `bonus.percent_of_trigger_rewards` flag (BONENG-913) — with it off the
   * "% of trigger amount" input never renders and rewards keep fixed $ only.
   */
  percentOfTriggerEnabled: boolean;
  /**
   * `bonus.multi_award_steps` flag (BONENG-1274) — with it on, follow-up
   * steps may use their own event and non-final AWARD steps continue.
   */
  multiAwardStepsEnabled: boolean;
  /**
   * `bonus.dynamic_bonus_builder` flag (BONENG-788) — with it on, Result
   * options use natural-language labels (no offer/award suffix).
   */
  naturalLanguageResultLabels: boolean;
  /**
   * `bonus.cashback_offers` flag (BONENG-432) — with it off, `award_cashback`
   * stays hidden from the Result picker.
   */
  cashbackOffersEnabled: boolean;
  /**
   * `bonus.credit_wallet_cash_result` flag (BONENG-1236) — with it on,
   * Refund on Loss is labeled Credit Wallet [cash] and the wallet
   * Restriction is hidden from authoring.
   */
  creditWalletCashResult: boolean;
  primaryCurrencyName: string;
  triggerMode: WizardTriggerMode;
  displayBannerV2Enabled: boolean;
  rulesLocked: boolean;
  /** View-only host — dim location pills; do not tie to rulesLocked. */
  readOnly: boolean;
  applyResultDefaults: (
    stepIndex: number,
    typeKey: string,
    previousTypeKey?: string,
  ) => void;
  /** Recompute placement defaults when a step's result or trigger changes. */
  applyDisplayLocationDefaults: (
    stepIndex: number,
    changed?: {
      resultTypeKey?: string;
      eventKey?: string;
    },
  ) => void;
};

/**
 * Trigger event / result / banner / conditions fields for a single step.
 * Shared by the flat (single-step) and numbered-card (2+ steps) layouts so
 * the body markup isn't duplicated between them.
 */
const StepFields: React.FC<StepFieldsProps> = ({
  field,
  isFirst,
  form,
  catalog,
  eventGroups,
  eventSelectOptions,
  stepOneEventKey,
  usesFixedStepOneTrigger,
  isCampaignStartTrigger,
  lockConditionsToInjected,
  stepsCount,
  isSeries,
  unlockResultOptions,
  injectedConditions,
  percentOfTriggerEnabled,
  multiAwardStepsEnabled,
  naturalLanguageResultLabels,
  cashbackOffersEnabled,
  creditWalletCashResult,
  primaryCurrencyName,
  triggerMode,
  displayBannerV2Enabled,
  rulesLocked,
  readOnly,
  applyResultDefaults,
  applyDisplayLocationDefaults,
}) => {
  const { isEnabled: futureBonusesFlag } = usePamFeature("futureBonusesPam");
  const futureBonusesEnabled = futureBonusesFlag === true;
  const { key: _key, ...restField } = field;
  const [restrictionDisplayNames, setRestrictionDisplayNames] = useState<
    Record<string, string>
  >({});
  const handleRestrictionDisplayName = useCallback((id: string, name: string) => {
    const trimmedId = id.trim();
    const trimmedName = name.trim();
    if (!trimmedId || !trimmedName || trimmedName === trimmedId) return;
    setRestrictionDisplayNames((prev) =>
      prev[trimmedId] === trimmedName ? prev : { ...prev, [trimmedId]: trimmedName },
    );
  }, []);
  const fixedTriggerLabel = isCampaignStartTrigger
    ? AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_STARTS_LABEL
    : displayNameFor(eventGroups, stepOneEventKey) || stepOneEventKey;
  const watchedResultTypeKey =
    (Form.useWatch(["steps", field.name, "result_type_key"], form) as
      | string
      | undefined) ?? "";
  const watchedRefundWallet = Form.useWatch(
    ["steps", field.name, "result_params", REFUND_ON_LOSS_WALLET_PARAM_KEY],
    form,
  );
  const {
    selectedSizingMode,
    setSelectedSizingMode,
    sizingCapOpen,
    setSizingCapOpen,
  } = useRewardSizingAuthoring(watchedResultTypeKey);
  const watchedResultType = catalog?.results?.find(
    (result) => result.type_key === watchedResultTypeKey,
  )?.result_type;
  const isDisplayBannerResult =
    watchedResultTypeKey === DISPLAY_BANNER_RESULT_TYPE_KEY;
  const displayLocationsField = (
    <div className={styles.displayLocationsSection}>
      <Form.Item
        {...restField}
        label={AppStrings.DISPLAY_LOCATIONS}
        tooltip={
          isDisplayBannerResult
            ? formFieldTooltip(
                AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_SURFACES_HINT,
              )
            : undefined
        }
        name={[field.name, "display_locations"]}
        rules={isDisplayBannerResult ? [displayLocationsRequiredRule] : undefined}
      >
        <DisplayLocationsField readOnly={readOnly} />
      </Form.Item>
    </div>
  );
  // "Step 1 Promo Applied" toggle (BONENG-962) — follow-up steps only.
  // Deliberately ships without a dedicated sub-flag (AGENTS.md §3 exception,
  // PAM review on PR #1890): the wizard is already gated behind the Dynamic
  // Bonuses flag, and default-on keeps submitted payloads identical to the
  // previous always-inject behavior until an operator opts out.
  // Mirrors buildCampaignBody's event resolution so what the operator sees
  // matches what submit injects.
  const watchedStepOneResultTypeKey = Form.useWatch(
    ["steps", 0, "result_type_key"],
    form,
  ) as string | undefined;
  const watchedOwnEventKey = Form.useWatch(
    ["steps", field.name, "event_key"],
    form,
  ) as string | undefined;
  const followUpEventKey = isFirst
    ? undefined
    : watchedOwnEventKey ??
      (isCampaignStartTrigger ? undefined : stepOneEventKey);
  const showAppliedOfferToggle =
    !isFirst &&
    appliedOfferToggleAvailable(watchedStepOneResultTypeKey, followUpEventKey);
  const appliedOfferEnabled =
    (Form.useWatch(
      ["steps", field.name, "applied_offer_matches_enabled"],
      form,
    ) as boolean | undefined) !== false;
  const showStepBannerEditor =
    !isDisplayBannerResult &&
    supportsStepBannerEditor(watchedResultTypeKey, watchedResultType);
  const requireBannerCopy =
    watchedResultType === "offer" ||
    (isFirst && isCampaignStartTrigger && watchedResultType === "award");

  // Conditionally required / visible params (requiredWhen / visibleWhen) live on
  // the grid when active, not as optional restriction chips — e.g. ThreeOaks.
  const chipParams = isDisplayBannerResult
    ? []
    : resultParamsForAuthoring(watchedResultTypeKey, {
        hideRefundWallet: creditWalletCashResult,
      }).filter(
    (param) =>
      !param.required &&
      !param.requiredWhen &&
      !param.visibleWhen &&
      !param.percentOfTrigger &&
      !param.percentCompanion &&
      !param.conditionsParam,
  );

  const restrictionsField =
    chipParams.length > 0 ? (
      <ShowBonusTargetingField
        form={form}
        stepFieldName={field.name}
        params={chipParams}
        resultTypeKey={watchedResultTypeKey}
        onRestrictionDisplayName={handleRestrictionDisplayName}
      />
    ) : null;

  const triggerEventField =
    isFirst && usesFixedStepOneTrigger ? (
      <Form.Item label={AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_EVENT}>
        <div className={styles.fixedTriggerBadgeWrap}>
          <StatusBadge
            tone="info"
            size="md"
            className={styles.fixedTriggerBadge}
            title={fixedTriggerLabel}
          >
            {fixedTriggerLabel}
          </StatusBadge>
        </div>
      </Form.Item>
    ) : (
      <>
        <Form.Item
          {...restField}
          label={AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_EVENT}
          name={[field.name, "event_key"]}
          dependencies={
            !isFirst && !multiAwardStepsEnabled
              ? isCampaignStartTrigger
                ? [["steps", 1, "event_key"]]
                : [["steps", 0, "event_key"]]
              : undefined
          }
          rules={[
            { required: true, message: AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_EVENT_REQUIRED },
            ...(!isFirst && !multiAwardStepsEnabled
              ? [
                  {
                    validator: (_: unknown, value?: string) => {
                      if (!value) return Promise.resolve();
                      const steps =
                        (form.getFieldValue("steps") as
                          | WizardStepFormValue[]
                          | undefined) ?? [];
                      const definesSharedFollowUp =
                        isCampaignStartTrigger && field.name === 1;
                      if (definesSharedFollowUp) return Promise.resolve();
                      const shared = isCampaignStartTrigger
                        ? steps[1]?.event_key
                        : stepOneEventKey;
                      if (!shared || value === shared) return Promise.resolve();
                      return Promise.reject(
                        new Error(AppStrings.PAM_DYNAMIC_BONUS_FOLLOW_UP_SAME_EVENT),
                      );
                    },
                  },
                ]
              : []),
          ]}
        >
          <Select
            placeholder={AppStrings.PAM_SEGMENTS_TRIGGER_EVENT_PLACEHOLDER}
            options={
              isCashbackResult(watchedResultTypeKey)
                ? cashbackEventSelectOptions(eventGroups)
                : eventSelectOptions
            }
            showSearch
            onChange={(eventKey: string) => {
              applyDisplayLocationDefaults(field.name, { eventKey });
              if (
                isCashbackResult(watchedResultTypeKey) &&
                isCashbackPeriodEvent(eventKey)
              ) {
                form.setFieldValue(
                  ["steps", field.name, "result_params", "calculation_period"],
                  cashbackPeriodForEventKey(eventKey),
                );
              }
            }}
          />
        </Form.Item>
        {!isFirst &&
          (multiAwardStepsEnabled ? (
            <p className={styles.stepHint}>
              {AppStrings.PAM_DYNAMIC_BONUS_FOLLOW_UP_OWN_EVENT}
            </p>
          ) : isCampaignStartTrigger ? (
            <p className={styles.stepHint}>
              {AppStrings.PAM_DYNAMIC_BONUS_FOLLOW_UP_SAME_EVENT}
            </p>
          ) : null)}
      </>
    );

  const resultField = (
    <Form.Item
      {...restField}
      label={AppStrings.PAM_DYNAMIC_BONUS_RESULT_LABEL}
      name={[field.name, "result_type_key"]}
      rules={[
        { required: true, message: AppStrings.PAM_DYNAMIC_BONUS_RESULT_TYPE_REQUIRED },
        // Backend invariant: a campaign_start step with an AWARD result must
        // be the campaign's only step (the award completes progress
        // immediately — follow-up steps would be unreachable). The "Add
        // step" button is hidden once step 1 is an AWARD (below), but that
        // doesn't retroactively clear steps added before the switch — this
        // is the belt-and-suspenders check for that path (BONENG-849).
        // Series (BONENG-1023) hit the same backend invariant, but their
        // step 2 is auto-removed/restored by an effect rather than a manual
        // button — this validator is the same belt-and-suspenders guard for
        // the brief window before that effect commits.
        ...(isFirst && (isCampaignStartTrigger || isSeries)
          ? [
              {
                validator: (_: unknown, value?: string) => {
                  const resultType = catalog?.results?.find(
                    (r) => r.type_key === value,
                  )?.result_type;
                  if (resultType === "award" && stepsCount > 1) {
                    return Promise.reject(
                      new Error(
                        "An award on campaign start completes the bonus — remove the follow-up steps first",
                      ),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]
          : []),
      ]}
    >
      <Select
        placeholder={AppStrings.PAM_DYNAMIC_BONUS_SELECT_RESULT_TYPE}
        options={
          catalog?.results
            ?.filter((r) =>
              r.type_key === watchedResultTypeKey ||
              isAuthorable(r, {
                cashbackOffersEnabled,
                triggerMode,
                futureBonusesEnabled,
                displayBannerV2Enabled,
              }),
            )
            .filter(
              (r) =>
                r.type_key === watchedResultTypeKey ||
                !(isFirst && isCampaignStartTrigger) ||
                isCampaignStartStepOneResult(r),
            )
            // Series step 1 allows show_bonus, Display Info Banner, or any
            // AWARD (BONENG-1023 / BONENG-1189). An AWARD on step 1 still
            // collapses the series to one step. Step 2 is award-only unless
            // Display Info Banner is in play: it can sit on any step, and
            // when step 1 is Display Info Banner the follow-up can be any
            // authorable result. Templates skip these locks.
            // An already-selected hidden type (award_free_cash, BONENG-1237)
            // stays listed so existing campaigns remain editable.
            .filter(
              (r) =>
                r.type_key === watchedResultTypeKey ||
                unlockResultOptions ||
                !isSeries ||
                (isFirst
                  ? isCampaignStartStepOneResult(r)
                  : r.result_type === "award" ||
                    r.type_key === DISPLAY_BANNER_RESULT_TYPE_KEY ||
                    watchedStepOneResultTypeKey ===
                      DISPLAY_BANNER_RESULT_TYPE_KEY),
            )
            // Surface the show_bonus OFFER first — it's the most common
            // result — while leaving the backend's order for the rest.
            .sort((a, b) => {
              if (a.type_key === "show_bonus") return -1;
              if (b.type_key === "show_bonus") return 1;
              return 0;
            })
            .map((r) => ({
              label: resultOptionLabel(
                r.type_key,
                r.result_type,
                naturalLanguageResultLabels,
                {
                  creditWalletCashResult,
                  primaryCurrencyName,
                  storedWallet:
                    r.type_key === REFUND_ON_LOSS_RESULT_TYPE_KEY
                      ? watchedRefundWallet
                      : undefined,
                  refundAlreadyAuthored:
                    r.type_key === REFUND_ON_LOSS_RESULT_TYPE_KEY &&
                    watchedResultTypeKey === REFUND_ON_LOSS_RESULT_TYPE_KEY,
                },
              ),
              value: r.type_key,
            })) ?? []
        }
        showSearch
        onChange={(value: string) =>
          applyResultDefaults(field.name, value, watchedResultTypeKey)
        }
      />
    </Form.Item>
  );

  const visibleInjected = isFirst
    ? injectedConditions.filter(
        (condition) => condition.type !== APPLIED_OFFER_MATCHES_TYPE,
      )
    : [];
  const injectedBlock =
    visibleInjected.length > 0 ? (
      <Form.Item label={AppStrings.PAM_DYNAMIC_BONUS_INCLUDED_CONDITIONS}>
        <div
          className={styles.conditionsList}
          data-testid="injected-conditions"
        >
          {visibleInjected.map((condition) => (
            <StatusBadge key={condition.type} tone="neutral" size="sm">
              {conditionDisplayLabel(condition.type)}
            </StatusBadge>
          ))}
        </div>
      </Form.Item>
    ) : null;

  // BONENG-962: operator-facing switch for applied_offer_matches. Default on
  // (undefined counts as enabled); the copy beside the switch names the
  // audience the step fires for in each state.
  const appliedOfferToggleBlock = showAppliedOfferToggle ? (
    <Form.Item>
      <div
        className={styles.appliedOfferToggleRow}
        data-testid="applied-offer-toggle"
      >
        <Form.Item
          {...restField}
          name={[field.name, "applied_offer_matches_enabled"]}
          noStyle
          getValueProps={(value) => ({ value: value !== false })}
        >
          <ToggleSwitch
            tone="info"
            ariaLabel={AppStrings.PAM_DYNAMIC_BONUS_APPLIED_OFFER_TOGGLE_ON}
          />
        </Form.Item>
        <span
          className={styles.appliedOfferToggleLabel}
          data-testid="applied-offer-toggle-state"
        >
          {appliedOfferEnabled
            ? AppStrings.PAM_DYNAMIC_BONUS_APPLIED_OFFER_TOGGLE_ON
            : AppStrings.PAM_DYNAMIC_BONUS_APPLIED_OFFER_TOGGLE_OFF}
        </span>
      </div>
    </Form.Item>
  ) : null;

  // Trigger Event and its Conditions share the left column; Result and its
  // Restrictions share the right — a single grid keeps both rows aligned
  // (Trigger Event above Conditions, Result above Restrictions). When the
  // step has a banner, its form fields / live preview extend the same two
  // columns (BONENG-874) instead of sitting in a second grid below — the
  // banner parts are handed in by StepBannerEditor's render prop.
  const renderStepGrid = (bannerParts?: {
    formFields: React.ReactNode;
    previewColumn: React.ReactNode;
  }) => (
    <Form.Item noStyle shouldUpdate>
      {({ getFieldValue }) => {
        const evtKey: string = isFirst
          ? stepOneEventKey
          : getFieldValue(["steps", field.name, "event_key"]) ?? "";
        const rtKey: string =
          getFieldValue(["steps", field.name, "result_type_key"]) ?? "";
        const isShowBonus = rtKey === "show_bonus";
        const stepConditions =
          (getFieldValue(["steps", field.name, "conditions"]) as
            | WizardStepFormValue["conditions"]
            | undefined) ?? [];
        const schema = RESULT_PARAM_SCHEMA[rtKey] ?? [];
        const resultParams = getFieldValue([
          "steps",
          field.name,
          "result_params",
        ]) as Record<string, unknown> | undefined;
        // "% of trigger amount" (BONENG-913): only offered when the flag is
        // on and the trigger event carries a monetary amount to size from.
        const percentContext = {
          eligible:
            percentOfTriggerEnabled &&
            PERCENT_OF_TRIGGER_EVENT_KEYS.has(evtKey),
          percentSet: ![undefined, null, ""].includes(
            resultParams?.[PERCENT_OF_TRIGGER_PARAM_KEY] as
              | string
              | undefined
              | null,
          ),
        };
        // Cross-field floor/cap for positive_amount params (BONENG-991) —
        // same shape as ShowBonusTargetingField so grid validators stay consistent.
        const TYPES_WITH_FLOOR = [
          "award_free_picks",
          "show_bonus",
          "create_profit_boost_promotion",
        ];
        let positiveAmountContext: PositiveAmountContext | undefined;
        if (TYPES_WITH_FLOOR.includes(rtKey)) {
          let perPickAmount: number | null = null;
          if (rtKey === "award_free_picks") {
            const amount = Number(resultParams?.amount);
            const split = Number(resultParams?.split_count) || 1;
            perPickAmount =
              Number.isFinite(amount) && amount > 0
                ? Math.floor((amount / split) * 100) / 100
                : null;
          }
          let maxWagerLimit: number | null = null;
          if (
            rtKey === "show_bonus" ||
            rtKey === "create_profit_boost_promotion"
          ) {
            const cap = Number(resultParams?.max_wager_limit);
            maxWagerLimit = Number.isFinite(cap) && cap > 0 ? cap : null;
          }
          positiveAmountContext = {
            perPickAmount,
            maxWagerLimit,
            percentOfTrigger: percentContext.percentSet,
          };
        }
        const rawMinLoss = resultParams?.min_loss_threshold;
        const minLoss = Number(rawMinLoss);
        const cashbackCapContext = isCashbackResult(rtKey)
          ? {
              minLossThreshold:
                rawMinLoss !== undefined &&
                rawMinLoss !== null &&
                rawMinLoss !== "" &&
                Number.isFinite(minLoss)
                  ? minLoss
                  : null,
            }
          : undefined;
        // The dollar/percent sizing fields are mutually exclusive (BONENG-976).
        // Flag-off / non-monetary events still disable the filled sibling;
        // BONENG-1171 renders one mode at a time when the selector is shown.
        const dollarAmountSet = schema.some(
          (param) =>
            param.replacedByPercent &&
            ![undefined, null, ""].includes(
              resultParams?.[param.key] as string | undefined | null,
            ),
        );
        // BONENG-1171: an award is sized one way or the other, so the fixed-$
        // and percent inputs are a mode choice rather than two fields to
        // reconcile — only the chosen one renders, and the cap hides behind a
        // toggle so the percent's optional extra never reads as a third sizing
        // field. Driven off the schema flags, so every result type pairing a
        // `replacedByPercent` field with a percent gets it (Cash, Free Pick,
        // Refund on Loss); the cap toggle appears only where a companion exists.
        const fixedAmountParam = schema.find((param) => param.replacedByPercent);
        const percentParam = schema.find((param) => param.percentOfTrigger);
        const capParam = schema.find((param) => param.percentCompanion);
        const showSizingModes = Boolean(
          percentContext.eligible && fixedAmountParam && percentParam,
        );
        const sizingMode =
          selectedSizingMode ??
          (percentContext.percentSet ? "percent" : "amount");
        const capSet = ![undefined, null, ""].includes(
          (capParam ? resultParams?.[capParam.key] : undefined) as
            | string
            | undefined
            | null,
        );
        const capVisible = sizingCapOpen ?? capSet;
        const clearResultParam = (key: string) =>
          form.setFieldValue(
            ["steps", field.name, "result_params", key],
            undefined,
          );
        const selectSizingMode = (mode: RewardSizingMode) => {
          if (mode === sizingMode) return;
          setSelectedSizingMode(mode);
          if (mode === "amount") {
            clearResultParam(PERCENT_OF_TRIGGER_PARAM_KEY);
            if (capParam) clearResultParam(capParam.key);
            setSizingCapOpen(null);
            return;
          }
          if (fixedAmountParam) clearResultParam(fixedAmountParam.key);
        };
        const toggleSizingCap = (next: boolean) => {
          setSizingCapOpen(next);
          if (!next && capParam) clearResultParam(capParam.key);
        };
        // Picking percent sizing shifts requiredness onto the percent input:
        // the fixed-$ field is unmounted in that mode, so leaving its `required`
        // rule live would block Next on an error the operator can't see.
        const percentRuleContext = showSizingModes
          ? {
              eligible: percentContext.eligible,
              percentSet: sizingMode === "percent",
              percentRequired: sizingMode === "percent",
            }
          : percentContext;
        const siblingMatches = (when?: { key: string; equals: string }) =>
          Boolean(
            when &&
              String(resultParams?.[when.key] ?? "") === when.equals,
          );
        const gridParams = schema.filter(
          (param) =>
            (param.required ||
              param.percentOfTrigger ||
              param.percentCompanion ||
              param.conditionsParam ||
              siblingMatches(param.requiredWhen) ||
              siblingMatches(param.visibleWhen)) &&
            (!(param.percentOfTrigger || param.percentCompanion) ||
              percentContext.eligible),
        );
        const splitConfirmation =
          rtKey === "award_free_picks" ? splitConfirmationInfo(resultParams) : null;
        const allSteps = getFieldValue("steps") as WizardStepFormValue[] | undefined;
        // Follow-up steps get applied_offer_matches re-injected at submit when
        // the step qualifies and the toggle is on — fold that in so the split
        // Free Picks guardrail doesn't ask operators to re-add what's already
        // included.
        const effectiveInjectedConditions = isFirst
          ? injectedConditions
          : injectedFollowUpConditionsFor(
              allSteps?.[0]?.result_type_key,
              evtKey,
              getFieldValue([
                "steps",
                field.name,
                "applied_offer_matches_enabled",
              ]) as boolean | undefined,
            );
        const authoringGuidance = splitAuthoringGuidanceText({
          resultTypeKey: rtKey,
          resultParams,
          eventKey: evtKey,
          conditions: [...stepConditions, ...effectiveInjectedConditions],
          steps: allSteps,
          isFirstStep: isFirst,
        });
        // show_bonus offers don't take free-form conditions — hide Add, and
        // omit the empty Conditions block so it doesn't leave dead space.
        // campaign_start step 1 never shows conditions (BONENG-959).
        // Affiliate referral step 1 is locked to injected badges only —
        // never render authored rows (hydrate + mode-switch clear them).
        const hideStepConditions = isFirst && isCampaignStartTrigger;
        const showConditionsList =
          !hideStepConditions &&
          !lockConditionsToInjected &&
          !(
            (isShowBonus || isDisplayBannerResult) &&
            stepConditions.length === 0
          );
        const showAwardConditionsBlock =
          gridParams.length > 0 ||
          Boolean(splitConfirmation) ||
          Boolean(authoringGuidance);
        const selectedEvent = eventForKey(eventGroups, evtKey);
        const conditionsField = showConditionsList ? (
          <ConditionsField
            listName={[field.name, "conditions"]}
            namePath={["steps", field.name, "conditions"]}
            catalog={catalog}
            fields={selectedEvent?.filterable_fields ?? []}
            eventKey={evtKey}
            applicableConditionTypes={selectedEvent?.applicable_condition_types}
            allowAdd={
              !isShowBonus && !isDisplayBannerResult && !lockConditionsToInjected
            }
            hideEmptyState={lockConditionsToInjected}
          />
        ) : null;
        const showTriggerConditionsBlock =
          !hideStepConditions && (Boolean(injectedBlock) || Boolean(conditionsField));
        return (
          <div className={styles.conditionsSection}>
            <Form.Item name={[field.name, "restriction_display_names"]} hidden>
              <RestrictionDisplayNamesControl />
            </Form.Item>
            <section className={styles.rulesCard}>
              <h3 className={styles.rulesCardTitle}>
                {AppStrings.PAM_DYNAMIC_BONUS_RULES_SECTION_TITLE}
              </h3>
              <div
                className={[
                  styles.conditionsRestrictionsGrid,
                  rulesLocked ? styles.readOnlyFields : "",
                ]
                  .join(" ")
                  .trim()}
                ref={(element) => {
                  if (rulesLocked) {
                    element?.setAttribute("inert", "");
                  } else {
                    element?.removeAttribute("inert");
                  }
                }}
                aria-disabled={rulesLocked || undefined}
                data-testid={rulesLocked ? "locked-campaign-rules" : undefined}
              >
                <div className={styles.conditionsRestrictionsCol}>
                  {triggerEventField}
                  {showTriggerConditionsBlock && (
                    <div
                      className={`${styles.awardConditionsBox} ${styles.rulesNestedBox}`}
                      data-testid="trigger-conditions-block"
                    >
                      {injectedBlock}
                      {conditionsField && (
                        <Form.Item
                          label={AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_RESTRICTIONS}
                          style={{ marginBottom: 0 }}
                        >
                          <div className={styles.conditionsColumnContent}>
                            {conditionsField}
                          </div>
                        </Form.Item>
                      )}
                    </div>
                  )}
                  {appliedOfferToggleBlock}
                </div>
                <div className={styles.conditionsRestrictionsCol}>
                  {resultField}
                  {multiAwardStepsEnabled &&
                    field.name < stepsCount - 1 &&
                    watchedResultType === "award" && (
                      <p className={styles.stepHint}>
                        {AppStrings.PAM_DYNAMIC_BONUS_AWARD_CONTINUES}
                      </p>
                    )}
                  {(restrictionsField || showAwardConditionsBlock) && (
                    <div
                      className={`${styles.awardConditionsBox} ${styles.rulesNestedBox}`}
                    >
                      <Form.Item
                        label={AppStrings.PAM_DYNAMIC_BONUS_RESULTS_RESTRICTIONS}
                        style={{ marginBottom: 0 }}
                      >
                        <div className={styles.conditionsColumnContent}>
                          {restrictionsField}
                          {showAwardConditionsBlock && (
                            <>
                              {gridParams.length > 0 && (
                                <div className={styles.resultParamsGrid}>
                                  {assembleRewardSizingGrid({
                                    gridParams,
                                    renderedParams: gridParams.map((param) => {
                                    // Split Count's per-unit preview (BONENG-883) sits
                                    // as a hover hint on the input itself — to the
                                    // right of the box the operator is editing —
                                    // rather than a separate block below the grid.
                                    // The suffix slot itself is always rendered (even
                                    // empty) for this field so the icon fading in/out
                                    // while typing doesn't toggle antd's prefix/suffix
                                    // DOM structure and steal focus mid-edit.
                                    const splitCountHint =
                                      param.key === "split_count" ? (
                                        <span className={styles.splitConfirmationHintSlot}>
                                          {splitConfirmation?.kind === "preview" && (
                                            <Tooltip title={splitConfirmation.text}>
                                              <QuestionCircleOutlined
                                                className={styles.splitConfirmationHint}
                                                aria-label={splitConfirmation.text}
                                                data-testid="split-confirmation-hint"
                                              />
                                            </Tooltip>
                                          )}
                                        </span>
                                      ) : undefined;
                                    // Amount / "% of trigger amount" mutual exclusion
                                    // (BONENG-976): the filled one disables the other,
                                    // so the operator must clear one before setting
                                    // the other. "Max amount" caps the percent, so it
                                    // unlocks only while a percent is set. The sizing
                                    // selector (BONENG-1171) renders one mode at a time
                                    // instead, so nothing there needs disabling to stay
                                    // exclusive.
                                    const isSizingParam = Boolean(
                                      param.replacedByPercent ||
                                        param.percentOfTrigger ||
                                        param.percentCompanion,
                                    );
                                    const paramDisabled = Boolean(
                                      !(showSizingModes && isSizingParam) &&
                                        ((param.percentOfTrigger && dollarAmountSet) ||
                                          (param.replacedByPercent &&
                                            percentContext.eligible &&
                                            percentContext.percentSet) ||
                                          (param.percentCompanion &&
                                            !percentContext.percentSet)),
                                    );
                                    // Clearing the percent leaves its companion cap
                                    // disabled — wipe the cap too so a stale value
                                    // can't linger in an uneditable field.
                                    const clearPercentCompanions = param.percentOfTrigger
                                      ? (event: React.ChangeEvent<HTMLInputElement>) => {
                                          if (event.target.value !== "") return;
                                          schema
                                            .filter((p) => p.percentCompanion)
                                            .forEach((p) => {
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", p.key],
                                                undefined,
                                              );
                                            });
                                        }
                                      : undefined;
                                    return (
                                      <Form.Item
                                        key={param.key}
                                        label={
                                          <span
                                            className={styles.resultParamLabelText}
                                            title={param.label}
                                          >
                                            {param.label}
                                          </span>
                                        }
                                        name={[field.name, "result_params", param.key]}
                                        validateTrigger={["onChange", "onBlur"]}
                                        rules={paramFieldRules(
                                          param,
                                          percentRuleContext,
                                          positiveAmountContext,
                                          resultParams as Record<string, unknown> | undefined,
                                          cashbackCapContext,
                                        )}
                                        dependencies={(() => {
                                          const siblingKeys =
                                            conditionalParamDependencyKeys(param);
                                          if (param.key === "max_cashback_amount") {
                                            siblingKeys.push("min_loss_threshold");
                                          }
                                          return siblingKeys.length > 0
                                            ? siblingKeys.map((key) => [
                                                "steps",
                                                field.name,
                                                "result_params",
                                                key,
                                              ])
                                            : undefined;
                                        })()}
                                        tooltip={param.help}
                                      >
                                        {param.type === "client-currency" ? (
                                          <ClientCurrencyField
                                            disabled={paramDisabled}
                                            provider={
                                              typeof resultParams?.provider === "string"
                                                ? resultParams.provider
                                                : undefined
                                            }
                                          />
                                        ) : param.type === "free-spin-provider" ? (
                                          <FreeSpinProviderSelect
                                            disabled={paramDisabled}
                                            onChange={(val) => {
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "provider"],
                                                val,
                                              );
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "game_ids"],
                                                "",
                                              );
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "currency"],
                                                undefined,
                                              );
                                              // ThreeOaks-only; drop stale dollars / type when provider changes.
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "value"],
                                                undefined,
                                              );
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "type"],
                                                val === "threeoaks" ? "fixed" : undefined,
                                              );
                                            }}
                                          />
                                        ) : param.type === "free-spin-games" ? (
                                          <FreeSpinGameSelect
                                            form={form}
                                            provider={
                                              typeof resultParams?.provider === "string"
                                                ? resultParams.provider
                                                : undefined
                                            }
                                            disabled={paramDisabled}
                                            restrictionDisplayNamesPath={[
                                              "steps",
                                              field.name,
                                              "restriction_display_names",
                                            ]}
                                            resultParamsPath={[
                                              "steps",
                                              field.name,
                                              "result_params",
                                            ]}
                                          />
                                        ) : param.type === "futures-sport" ? (
                                          <FutureBonusSportSelect
                                            disabled={paramDisabled}
                                            onChange={(val) => {
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "sport_id"],
                                                val,
                                              );
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "league_id"],
                                                undefined,
                                              );
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "trait_id"],
                                                undefined,
                                              );
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "trait_name"],
                                                undefined,
                                              );
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "market_settlement_date"],
                                                undefined,
                                              );
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "eligible_market_types"],
                                                undefined,
                                              );
                                            }}
                                          />
                                        ) : param.type === "futures-league" ? (
                                          <FutureBonusLeagueSelect
                                            disabled={paramDisabled}
                                            sportId={
                                              typeof resultParams?.sport_id === "string"
                                                ? resultParams.sport_id
                                                : undefined
                                            }
                                            onChange={(val) => {
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "league_id"],
                                                val,
                                              );
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "trait_id"],
                                                undefined,
                                              );
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "trait_name"],
                                                undefined,
                                              );
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "market_settlement_date"],
                                                undefined,
                                              );
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "eligible_market_types"],
                                                undefined,
                                              );
                                            }}
                                          />
                                        ) : param.type === "futures-trait" ? (
                                          <FutureBonusTraitSelect
                                            disabled={paramDisabled}
                                            sportId={
                                              typeof resultParams?.sport_id === "string"
                                                ? resultParams.sport_id
                                                : undefined
                                            }
                                            leagueId={
                                              typeof resultParams?.league_id === "string"
                                                ? resultParams.league_id
                                                : undefined
                                            }
                                            onTraitResolved={(next) => {
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "trait_name"],
                                                next.trait_name,
                                              );
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "market_settlement_date"],
                                                next.market_settlement_date,
                                              );
                                              form.setFieldValue(
                                                ["steps", field.name, "result_params", "eligible_market_types"],
                                                next.eligible_market_types,
                                              );
                                            }}
                                          />
                                        ) : param.type === "readonly" ? (
                                          <FutureBonusSettlementDisplay
                                            value={
                                              typeof resultParams?.market_settlement_date === "string"
                                                ? resultParams.market_settlement_date
                                                : undefined
                                            }
                                          />
                                        ) : param.type === "datetime" ? (
                                          <FutureBonusExpiryPicker disabled={paramDisabled} />
                                        ) : param.type === "select" ? (
                                          <Select
                                            placeholder={formatString(
                                              AppStrings.PAM_DYNAMIC_BONUS_SELECT_FIELD,
                                              { title: param.label },
                                            )}
                                            options={param.options}
                                            onChange={(value: string) => {
                                              if (
                                                param.key !== "calculation_period" ||
                                                !isCashbackCalculationPeriod(value)
                                              ) {
                                                return;
                                              }
                                              const eventKey =
                                                eventKeyForCashbackPeriod(value);
                                              form.setFieldValue(
                                                ["steps", field.name, "event_key"],
                                                eventKey,
                                              );
                                              applyDisplayLocationDefaults(field.name, {
                                                eventKey,
                                              });
                                            }}
                                          />
                                        ) : (
                                          <Input
                                            placeholder={param.placeholder}
                                            suffix={splitCountHint}
                                            disabled={paramDisabled}
                                            onChange={clearPercentCompanions}
                                          />
                                        )}
                                      </Form.Item>
                                    );
                                    }),
                                    showSizingModes,
                                    sizingMode,
                                    capVisible,
                                    fixedAmountParam,
                                    percentParam,
                                    capParam,
                                    onSelectMode: selectSizingMode,
                                    onToggleCap: toggleSizingCap,
                                  })}
                                </div>
                              )}
                              {splitConfirmation?.kind === "warning" && (
                                <div className={styles.infoBox} role="status">
                                  {splitConfirmation.text}
                                </div>
                              )}
                              {authoringGuidance && (
                                <div
                                  className={styles.infoBox}
                                  role="status"
                                  data-testid="split-authoring-guidance"
                                >
                                  {authoringGuidance}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </Form.Item>
                    </div>
                  )}
                </div>
              </div>
            </section>
            {bannerParts && (
              <section
                className={styles.displayCard}
                data-testid={
                  rtKey === DISPLAY_BANNER_RESULT_TYPE_KEY
                    ? "display-banner-step"
                    : undefined
                }
              >
                <h3 className={styles.displayCardTitle}>
                  {AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_SECTION_TITLE}
                </h3>
                <div className={styles.conditionsRestrictionsGrid}>
                  <div className={styles.conditionsRestrictionsCol}>
                    {bannerParts.formFields}
                  </div>
                  <div className={styles.conditionsRestrictionsCol}>
                    {bannerParts.previewColumn}
                  </div>
                </div>
              </section>
            )}
          </div>
        );
      }}
    </Form.Item>
  );

  if (showStepBannerEditor) {
    return (
      <StepBannerEditor
        namePath={["steps", field.name, "banner"]}
        itemNamePath={[field.name, "banner"]}
        form={form}
        requireCopy={requireBannerCopy}
        belowPreview={displayLocationsField}
        enableWhenFormDisabled={rulesLocked}
        resultParamsPath={["steps", field.name, "result_params"]}
        conditionsPath={["steps", field.name, "conditions"]}
        restrictionDisplayNames={restrictionDisplayNames}
      >
        {({ formFields, previewColumn }) =>
          renderStepGrid({
            formFields: rulesLocked ? (
              <ConfigProvider componentDisabled={false}>
                {formFields}
              </ConfigProvider>
            ) : (
              formFields
            ),
            previewColumn: rulesLocked ? (
              <ConfigProvider componentDisabled={false}>
                {previewColumn}
              </ConfigProvider>
            ) : (
              previewColumn
            ),
          })}
      </StepBannerEditor>
    );
  }

  if (isDisplayBannerResult) {
    return (
      <>
        <Form.Item
          name={[field.name, "freeform_layout"]}
          hidden
          initialValue={DEFAULT_FREEFORM_BANNER_LAYOUT}
        >
          <FormObjectAnchor />
        </Form.Item>
        <FreeformBannerEditor
          form={form}
          bannerPath={["steps", field.name, "banner"]}
          bannerItemPath={[field.name, "banner"]}
          layoutPath={["steps", field.name, "freeform_layout"]}
          belowPreview={displayLocationsField}
          imageRequired
        >
          {({ formFields, previewColumn }) =>
            renderStepGrid({
              formFields: rulesLocked ? (
                <ConfigProvider componentDisabled={false}>
                  {formFields}
                </ConfigProvider>
              ) : (
                formFields
              ),
              previewColumn: rulesLocked ? (
                <ConfigProvider componentDisabled={false}>
                  {previewColumn}
                </ConfigProvider>
              ) : (
                previewColumn
              ),
            })
          }
        </FreeformBannerEditor>
      </>
    );
  }

  return renderStepGrid();
};

/**
 * Combined trigger + steps step. Legacy campaigns with `trigger_mode: "none"`
 * still render conditions-only; new campaigns always pick a real trigger.
 */
const TriggerStepsStep: React.FC<Props> = ({
  form,
  catalog,
  eventGroups,
  stepOneEventKey,
  onNext,
  onPrev,
  showFooter = true,
  rulesLocked = false,
  readOnly = false,
}) => {
  /** Indexes the operator has collapsed — all others render open. */
  const [closedStepIndexes, setClosedStepIndexes] = useState<Set<number>>(
    () => new Set(),
  );

  // Module-level titles would otherwise leak from campaign A onto campaign B
  // for the same Sportradar ID.
  useEffect(() => () => {
    clearRestrictionDisplayNames();
  }, []);

  const campaignType =
    (Form.useWatch("campaign_type", { form, preserve: true }) as
      | WizardCampaignType
      | undefined) ?? DEFAULT_CAMPAIGN_TYPE;
  const { isEnabled: displayBannerV2Flag } = usePamFeature("displayBannerV2");
  const displayBannerV2Enabled = displayBannerV2Flag === true;
  const isSeries = campaignType === SERIES_CAMPAIGN_TYPE;
  const isDisplayBanner =
    campaignType === DISPLAY_BANNER_CAMPAIGN_TYPE && !displayBannerV2Enabled;
  const watchedBetTargetedPreset = Form.useWatch("bet_targeted_preset", {
    form,
    preserve: true,
  });
  const betTargetedPreset: WizardBetTargetedPreset | null = isWizardBetTargetedPreset(
    watchedBetTargetedPreset,
  )
    ? watchedBetTargetedPreset
    : null;
  const createdFromTemplateId = Form.useWatch("created_from_template_id", {
    form,
    preserve: true,
  }) as string | null | undefined;
  const unlockResultOptions = Boolean(createdFromTemplateId);
  const isFixedBlueprint = isSeries;
  const { isEnabled: percentOfTriggerFlag } = usePamFeature(
    "percentOfTriggerRewards",
  );
  const percentOfTriggerEnabled = percentOfTriggerFlag === true;
  const { isEnabled: multiAwardStepsFlag } = usePamFeature("multiAwardSteps");
  const multiAwardStepsEnabled = multiAwardStepsFlag === true;
  const { isEnabled: dynamicBonusBuilderFlag } = usePamFeature(
    "dynamicBonusBuilder",
  );
  const naturalLanguageResultLabels = dynamicBonusBuilderFlag === true;
  const { isEnabled: displayBannerEnabled } = usePamFeature(
    "standaloneDisplayBanner",
  );
  const { isEnabled: cashbackOffersFlag } = usePamFeature("cashbackOffers");
  const cashbackOffersEnabled = cashbackOffersFlag === true;
  const {
    isEnabled: creditWalletCashResultFlag,
    loading: creditWalletCashResultLoading,
  } = usePamFeature("creditWalletCashResult");
  const creditWalletCashResult = creditWalletCashResultFlag === true;
  const creditWalletFlagResolved =
    creditWalletCashResultFlag !== null && !creditWalletCashResultLoading;
  const primaryCurrencyName = usePrimaryCurrencyName();
  const { isEnabled: dynamicBonusesBetaMode } = usePamFeature(
    "dynamicBonusesBetaMode",
  );
  const referralCampaignTypesEnabled = dynamicBonusesBetaMode === true;

  const watchedTriggerMode = Form.useWatch("trigger_mode", {
    form,
    preserve: true,
  }) as WizardTriggerMode | undefined;
  // Prefer the store value when useWatch hasn't caught up yet (field mounts
  // late in the wizard). Fall back to "event" — "none" is no longer selectable.
  const triggerMode: WizardTriggerMode =
    watchedTriggerMode ??
    (form.getFieldValue("trigger_mode") as WizardTriggerMode | undefined) ??
    "event";
  const affiliateReferralCode = Form.useWatch("affiliate_referral_code", {
    form,
    preserve: true,
  }) as string | undefined;

  const [affiliateSearch, setAffiliateSearch] = useState("");
  const [selectedAffiliateId, setSelectedAffiliateId] = useState<string | undefined>(
    () => form.getFieldValue("affiliate_id") as string | undefined,
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: affiliatesData, isFetching: affiliatesLoading } = useAffiliatesList({
    search: affiliateSearch,
    limit: 20,
    status: "active",
  });

  const { data: promoCodesData, isFetching: promoCodesLoading } = useAffiliatePromoCodes(
    selectedAffiliateId,
    { status: "active" },
  );

  // campaign_start is reachable only via the dedicated trigger-mode pill +
  // fixed step-1 badge — never offered in the free-text event picker, so it
  // can't be mistakenly assigned to step 1 in "event" mode or to any step 2+
  // (backend rejects it there; rule 1 of the authoring contract).
  const eventSelectOptions = useMemo(() => {
    const withoutCampaignStart = eventGroups
      .map((group) => ({
        ...group,
        events: group.events.filter((e) => e.event_key !== CAMPAIGN_START_EVENT_KEY),
      }))
      .filter((group) => group.events.length > 0);
    return toGroupedSelectOptions(withoutCampaignStart);
  }, [eventGroups]);

  // When the catalog only exposes a single selectable event, there is nothing
  // for the admin to choose between — auto-select it so they don't have to
  // open the dropdown just to pick the only option.
  const onlyEventOption = useMemo(() => {
    const flatOptions = eventSelectOptions.flatMap((group) => group.options);
    return flatOptions.length === 1 ? flatOptions[0].value : undefined;
  }, [eventSelectOptions]);

  // The campaign_start pill is data-driven: it only appears once the backend
  // flag `bonus.campaign_start_trigger` is on and the events catalog exposes
  // the key, so a merged-but-unreleased backend never breaks the UI.
  const campaignStartAvailable = useMemo(
    () => hasCampaignStartEvent(eventGroups),
    [eventGroups],
  );

  // Coerce legacy/unavailable trigger modes and re-write the store so
  // useWatch (which can miss late-mounted fields) observes a selectable value.
  useEffect(() => {
    const current = form.getFieldValue("trigger_mode") as WizardTriggerMode | undefined;
    const fallback: WizardTriggerMode = campaignStartAvailable
      ? "campaign_start"
      : "event";
    const next = isDisplayBanner
      ? "campaign_start"
      : !current ||
          current === "none" ||
          (current === "campaign_start" && !campaignStartAvailable)
        ? fallback
        : current;
    form.setFieldValue("trigger_mode", next);
  }, [form, campaignStartAvailable, isDisplayBanner]);

  const visibleTriggerModes = useMemo(
    () =>
      TRIGGER_MODES().filter((m) => {
        if (m.mode === "campaign_start" && !campaignStartAvailable) return false;
        if (
          BETA_HIDDEN_TRIGGER_MODES.has(m.mode) &&
          !referralCampaignTypesEnabled &&
          triggerMode !== m.mode
        ) {
          return false;
        }
        return true;
      }),
    [campaignStartAvailable, referralCampaignTypesEnabled, triggerMode],
  );

  const affiliateOptions = useMemo(
    () =>
      (affiliatesData?.items ?? []).map((a) => ({
        value: a.affiliate_id,
        label: a.company_name || a.contact_name || a.email || a.affiliate_id,
      })),
    [affiliatesData],
  );

  const promoCodeOptions = useMemo(
    () =>
      (promoCodesData?.items ?? [])
        .filter((c) => !c.disabled)
        .map((c) => ({
          value: c.promo_code,
          label: c.promo_name ? `${c.promo_code} — ${c.promo_name}` : c.promo_code,
        })),
    [promoCodesData],
  );

  const injectedConditions = useMemo(
    () => injectedStepConditionsFor(triggerMode, affiliateReferralCode),
    [triggerMode, affiliateReferralCode],
  );

  const handleAffiliateSearch = useCallback((value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setAffiliateSearch(value), 300);
  }, []);

  const handleAffiliateSelect = useCallback(
    (affiliateId: string) => {
      setSelectedAffiliateId(affiliateId);
      form.setFieldValue("affiliate_referral_code", undefined);
    },
    [form],
  );

  const handleModeSelect = useCallback(
    (mode: WizardTriggerMode) => {
      if (
        form.getFieldValue("campaign_type") ===
        DISPLAY_BANNER_CAMPAIGN_TYPE
      ) {
        form.setFieldsValue(
          wizardInitialValuesForCampaignType(DEFAULT_CAMPAIGN_TYPE) as never,
        );
      }
      form.setFieldValue("trigger_mode", mode);
      const steps = form.getFieldValue("steps") as WizardStepFormValue[] | undefined;
      // Campaign type owns the placement strategy, so switching type
      // intentionally replaces any locations customized under the old type.
      steps?.forEach((step, index) => {
        form.setFieldValue(
          ["steps", index, "display_locations"],
          defaultDisplayLocations({
            resultTypeKey: step.result_type_key,
            eventKey: step.event_key,
            triggerMode: mode,
          }),
        );
      });
      if (mode === "affiliate_referral") {
        const current = form.getFieldValue("max_awards_per_user");
        if (current === undefined || current === "") {
          form.setFieldValue("max_awards_per_user", "1");
        }
        // BONENG-959: affiliate step 1 is injected-only — clear any authored
        // free-form conditions left over from another trigger mode.
        form.setFieldValue(["steps", 0, "conditions"], []);
      }
      if (mode === "campaign_start") {
        form.setFieldValue(["steps", 0, "conditions"], []);
      }
      if (mode === "event" || mode === "campaign_start") {
        form.setFieldValue("trigger_event_key", undefined);
      }
    },
    [form],
  );

  const handleDisplayBannerSelect = useCallback(() => {
    form.setFieldsValue(
      wizardInitialValuesForCampaignType(DISPLAY_BANNER_CAMPAIGN_TYPE) as never,
    );
  }, [form]);

  const applyDisplayLocationDefaults = useCallback(
    (
      stepIndex: number,
      changed?: {
        resultTypeKey?: string;
        eventKey?: string;
      },
    ) => {
      const triggerMode = form.getFieldValue("trigger_mode") as
        | WizardTriggerMode
        | undefined;
      form.setFieldValue(
        ["steps", stepIndex, "display_locations"],
        defaultDisplayLocations({
          resultTypeKey:
            changed?.resultTypeKey ??
            form.getFieldValue(["steps", stepIndex, "result_type_key"]),
          eventKey:
            changed?.eventKey ??
            form.getFieldValue(["steps", stepIndex, "event_key"]),
          triggerMode,
        }),
      );
    },
    [form],
  );

  const applyResultDefaults = useCallback(
    (stepIndex: number, typeKey: string, previousTypeKey?: string) => {
      // Leaving Cashback must not keep cashback_period_end_* — that key is
      // hidden from the Trigger Event list and would still submit. Shared by
      // Display Info Banner's early return and the generic Result switch.
      const restoreNonCashbackEventIfNeeded = (): {
        didRestore: boolean;
        eventKey?: string;
      } => {
        if (isCashbackResult(typeKey)) return { didRestore: false };
        if (
          !isCashbackPeriodEvent(
            form.getFieldValue(["steps", stepIndex, "event_key"]),
          )
        ) {
          return { didRestore: false };
        }
        const eventKey = eventGroups
          .flatMap((group) => group.events)
          .find((event) => !isCashbackPeriodEvent(event.event_key))?.event_key;
        form.setFieldValue(["steps", stepIndex, "event_key"], eventKey);
        return { didRestore: true, eventKey };
      };

      if (typeKey !== DISPLAY_BANNER_RESULT_TYPE_KEY) {
        form.setFieldValue(["steps", stepIndex, "freeform_layout"], undefined);
      } else {
        form.setFieldValue(
          ["steps", stepIndex, "freeform_layout"],
          DEFAULT_FREEFORM_BANNER_LAYOUT,
        );
        form.setFieldValue(["steps", stepIndex, "result_params"], undefined);
        form.setFieldValue(["steps", stepIndex, "banner"], {
          claim: {
            visible: DEFAULT_DISPLAY_BANNER_CLAIM.visible,
            text: {
              en: AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_BUTTON_PLACEHOLDER,
            },
            text_color: DEFAULT_DISPLAY_BANNER_CLAIM.text_color,
            background_color: [...DEFAULT_DISPLAY_BANNER_CLAIM.background_color],
            border_color: DEFAULT_DISPLAY_BANNER_CLAIM.border_color,
          },
        });
        const { didRestore, eventKey } = restoreNonCashbackEventIfNeeded();
        applyDisplayLocationDefaults(stepIndex, {
          resultTypeKey: typeKey,
          ...(didRestore ? { eventKey } : {}),
        });
        return;
      }
      const schema = resultParamsForAuthoring(typeKey, {
        hideRefundWallet: creditWalletCashResult,
      });
      const allowedParamKeys = new Set(schema.map((param) => param.key));
      allowedParamKeys.add(RESTRICTION_DISPLAY_NAMES_PARAM_KEY);
      // Keep wallet on the payload even though the Restriction chip is hidden.
      // A stored promo wallet must survive a same-type Result re-pick so live
      // No Sweat Bet campaigns keep issuing a Free Pick. New Credit Wallet
      // selections (no legacy wallet) persist rebet_cash.
      if (
        creditWalletCashResult &&
        typeKey === REFUND_ON_LOSS_RESULT_TYPE_KEY
      ) {
        allowedParamKeys.add(REFUND_ON_LOSS_WALLET_PARAM_KEY);
      }
      const currentParams =
        (form.getFieldValue(["steps", stepIndex, "result_params"]) as
          | Record<string, unknown>
          | undefined) ?? {};
      form.setFieldValue(
        ["steps", stepIndex, "result_params"],
        Object.fromEntries(
          Object.entries(currentParams).filter(([key]) => allowedParamKeys.has(key)),
        ),
      );
      if (creditWalletCashResult && typeKey === REFUND_ON_LOSS_RESULT_TYPE_KEY) {
        const storedWallet = currentParams[REFUND_ON_LOSS_WALLET_PARAM_KEY];
        const isRepick = previousTypeKey === REFUND_ON_LOSS_RESULT_TYPE_KEY;
        // Re-picking an existing refund (promo or omitted wallet) must keep
        // the Free Pick path. Only a new Credit Wallet selection writes cash.
        if (
          !isLegacyRefundPromoWallet(storedWallet) &&
          !(isRepick && storedWallet !== PRIMARY_CASH_WALLET)
        ) {
          form.setFieldValue(
            ["steps", stepIndex, "result_params", REFUND_ON_LOSS_WALLET_PARAM_KEY],
            PRIMARY_CASH_WALLET,
          );
        }
      }
      schema.forEach((param) => {
        const defaultValue = param.default;
        // The title is refreshed unconditionally below, so a blank-only fill
        // here would be dead weight. Conditionally visible params (ThreeOaks
        // value/type) wait until the sibling provider matches.
        if (
          defaultValue === undefined ||
          param.key === TRANSACTION_TITLE_PARAM_KEY ||
          param.requiredWhen ||
          param.visibleWhen
        ) {
          return;
        }
        const path = ["steps", stepIndex, "result_params", param.key];
        const current = form.getFieldValue(path);
        if (current === undefined || current === "") {
          form.setFieldValue(path, defaultValue);
        }
      });
      if (isCashbackResult(typeKey)) {
        const periodRaw = form.getFieldValue([
          "steps",
          stepIndex,
          "result_params",
          "calculation_period",
        ]);
        const period = isCashbackCalculationPeriod(periodRaw)
          ? periodRaw
          : "weekly";
        if (!isCashbackCalculationPeriod(periodRaw)) {
          form.setFieldValue(
            ["steps", stepIndex, "result_params", "calculation_period"],
            period,
          );
        }
        const eventKey = eventKeyForCashbackPeriod(period);
        if (form.getFieldValue(["steps", stepIndex, "event_key"]) !== eventKey) {
          form.setFieldValue(["steps", stepIndex, "event_key"], eventKey);
          applyDisplayLocationDefaults(stepIndex, {
            resultTypeKey: typeKey,
            eventKey,
          });
        }
      } else {
        const { didRestore, eventKey } = restoreNonCashbackEventIfNeeded();
        if (didRestore) {
          applyDisplayLocationDefaults(stepIndex, {
            resultTypeKey: typeKey,
            eventKey,
          });
        }
      }
      // BONENG-960: the title names the reward, so it follows the result type
      // instead of keeping the previous one's. Clearing it for types with no
      // default stops a stale title from submitting under a result that writes
      // no ledger row.
      form.setFieldValue(
        ["steps", stepIndex, "result_params", TRANSACTION_TITLE_PARAM_KEY],
        DEFAULT_TRANSACTION_TITLES[typeKey],
      );
      const resultType = catalog?.results?.find(
        (result) => result.type_key === typeKey,
      )?.result_type;
      if (supportsStepBannerEditor(typeKey, resultType)) {
        const bannerPath = ["steps", stepIndex, "banner"];
        const currentBanner = form.getFieldValue(bannerPath) as
          | BannerIn
          | undefined;
        const defaultTitle = schema.find(
          (param) => param.key === "banner_title_key",
        )?.default;
        const defaultMessage = schema.find(
          (param) => param.key === "banner_msg_key",
        )?.default;
        const bannerWithDefaultCopy: BannerIn = {
          ...currentBanner,
          ...(typeof defaultTitle === "string" &&
          !currentBanner?.title?.text?.en
            ? {
                title: {
                  ...currentBanner?.title,
                  text: {
                    ...currentBanner?.title?.text,
                    en: defaultTitle,
                  },
                },
              }
            : {}),
          ...(typeof defaultMessage === "string" &&
          !currentBanner?.short_description?.text?.en
            ? {
                short_description: {
                  ...currentBanner?.short_description,
                  text: {
                    ...currentBanner?.short_description?.text,
                    en: defaultMessage,
                  },
                },
              }
            : {}),
        };
        form.setFieldValue(
          bannerPath,
          initializeBannerThemePreset(bannerWithDefaultCopy, "brand"),
        );
      }
      if (supportsStepBannerEditor(typeKey, resultType)) {
        applyDisplayLocationDefaults(stepIndex, { resultTypeKey: typeKey });
      } else {
        form.setFieldValue(["steps", stepIndex, "display_locations"], undefined);
      }
    },
    [
      applyDisplayLocationDefaults,
      catalog?.results,
      creditWalletCashResult,
      eventGroups,
      form,
    ],
  );

  // Apply the named blueprint only on a blank wizard (create / empty steps).
  // Never rewrite after hydrate or after the operator removes a follow-up —
  // a missing step-2 result is not permission to restore the blueprint.
  useEffect(() => {
    if (!betTargetedPreset) return;
    if (!creditWalletFlagResolved) return;
    const steps = form.getFieldValue("steps") as WizardStepFormValue[] | undefined;
    const stepOneUnauthored = !steps?.[0]?.result_type_key;
    const stepTwoUnauthored = !steps?.[1]?.result_type_key;
    if (!stepOneUnauthored || !stepTwoUnauthored) return;
    form.setFieldValue(
      "steps",
      stepsForBetTargetedPreset(betTargetedPreset, {
        seedPrimaryCashWallet: creditWalletCashResult,
      }),
    );
    applyResultDefaults(0, "show_bonus");
  }, [
    applyResultDefaults,
    betTargetedPreset,
    creditWalletCashResult,
    creditWalletFlagResolved,
    form,
  ]);

  // Seed result/trigger-specific locations for preset blueprints and older
  // drafts. Explicitly authored locations remain unchanged until the result or
  // trigger changes.
  const watchedStepResultKeys = (
    (Form.useWatch("steps", { form, preserve: true }) as
      | WizardStepFormValue[]
      | undefined) ?? []
  )
    .map((step) => step?.result_type_key ?? "")
    .join(",");
  useEffect(() => {
    const steps = form.getFieldValue("steps") as WizardStepFormValue[] | undefined;
    steps?.forEach((step, index) => {
      const typeKey = step?.result_type_key;
      if (!typeKey || step?.display_locations != null) return;
      const resultType = catalog?.results?.find(
        (result) => result.type_key === typeKey,
      )?.result_type;
      if (isInformResultType(resultType) || resultType === "award") {
        applyDisplayLocationDefaults(index);
      }
    });
  }, [applyDisplayLocationDefaults, catalog?.results, form, watchedStepResultKeys]);

  const toggleStep = useCallback((index: number) => {
    setClosedStepIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const usesFixedReferralTrigger =
    triggerMode === "referral" || triggerMode === "affiliate_referral";
  const isCampaignStartTrigger = triggerMode === "campaign_start";
  const usesFixedStepOneTrigger = usesFixedReferralTrigger || isCampaignStartTrigger;
  const isNoneTrigger = triggerMode === "none";

  const watchedSteps = (Form.useWatch("steps", { form, preserve: true }) as
    | WizardStepFormValue[]
    | undefined) ?? [];

  // Backend invariant: a campaign_start step with an AWARD result must be the
  // campaign's only step — the award completes progress immediately, so any
  // follow-up step would be unreachable (BONENG-849). Mirrored in the wizard
  // by hiding "Add step" below once step 1 resolves to an AWARD.
  const stepOneResultTypeKey = watchedSteps[0]?.result_type_key;
  const stepOneResultType = catalog?.results?.find(
    (r) => r.type_key === stepOneResultTypeKey,
  )?.result_type;
  const isAwardOnCampaignStartStepOne =
    isCampaignStartTrigger && stepOneResultType === "award";
  const isDisplayBannerOnSeriesStepOne =
    isSeries &&
    displayBannerV2Enabled &&
    stepOneResultTypeKey === DISPLAY_BANNER_RESULT_TYPE_KEY;
  // Braze is a fixed two-step blueprint except Display Info Banner, where
  // the follow-up step is optional (BONENG-1189).
  const lockSeriesStepCount = isSeries && !isDisplayBannerOnSeriesStepOne;

  // Series-specific mirror of the invariant above (BONENG-1023): a series'
  // step 1 resolving to an AWARD also completes the whole series in one
  // step. Series hide the manual Add/Remove step controls entirely
  // (`lockSeriesStepCount`), so — unlike the free-form campaign_start case,
  // where the operator manually removes the follow-up step before it's
  // blocked by validation — this effect auto-drops step 2 the moment step 1
  // becomes an AWARD, and restores an empty step 2 if it switches back to
  // show_bonus, so the form always matches what the campaign type allows.
  // Display Info Banner does not auto-add step 2; an empty seeded follow-up
  // is dropped when the operator first picks it.
  const isAwardOnSeriesStepOne = isSeries && stepOneResultType === "award";
  const prevSeriesStepOneKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!isSeries || unlockResultOptions) return;
    const steps = (form.getFieldValue("steps") as WizardStepFormValue[] | undefined) ?? [];
    const stepOneKey = steps[0]?.result_type_key;
    const becameDisplayBanner =
      displayBannerV2Enabled &&
      stepOneKey === DISPLAY_BANNER_RESULT_TYPE_KEY &&
      prevSeriesStepOneKeyRef.current !== DISPLAY_BANNER_RESULT_TYPE_KEY;
    prevSeriesStepOneKeyRef.current = stepOneKey;

    if (isAwardOnSeriesStepOne) {
      if (steps.length > 1) {
        form.setFieldValue("steps", steps.slice(0, 1));
      }
      return;
    }
    if (becameDisplayBanner) {
      const followUps = steps.slice(1);
      // Auto-filled follow-up event_key (only-option catalog) is not operator
      // intent — a step is unused until a Result is picked.
      if (
        followUps.length > 0 &&
        followUps.every((step) => !step?.result_type_key)
      ) {
        form.setFieldValue("steps", steps.slice(0, 1));
      }
      return;
    }
    if (stepOneKey !== DISPLAY_BANNER_RESULT_TYPE_KEY && steps.length < 2) {
      form.setFieldValue("steps", [...steps, {}]);
    }
  }, [
    displayBannerV2Enabled,
    form,
    isAwardOnSeriesStepOne,
    isSeries,
    stepOneResultTypeKey,
    unlockResultOptions,
  ]);

  useEffect(() => {
    if (!onlyEventOption || isNoneTrigger) return;
    const steps =
      (form.getFieldValue("steps") as WizardStepFormValue[] | undefined) ?? [];
    steps.forEach((step, index) => {
      const isFirst = index === 0;
      if (isFirst && usesFixedStepOneTrigger) return;
      if (!step?.event_key) {
        form.setFieldValue(["steps", index, "event_key"], onlyEventOption);
      }
    });
    // Live `steps` from the form — not `watchedSteps` — so this cannot
    // resurrect a follow-up that the series-shape effect just dropped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyEventOption, isNoneTrigger, usesFixedStepOneTrigger, form, watchedSteps.length]);

  return (
    <>
      <Form.Item name="trigger_mode" hidden>
        <Input />
      </Form.Item>

      {isAwardOnSeriesStepOne && (
        <p className={styles.stepHint} data-testid="series-single-step-hint">
          An award on Step 1 completes the series in one step — Step 2 is removed
          automatically. Switch the result back to show_bonus to restore it.
        </p>
      )}
      {!isFixedBlueprint && (
        <>
          <Form.Item
            label={AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_TYPE_LABEL}
            tooltip={formFieldTooltip(AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_TYPE_TOOLTIP)}
            className={styles.campaignTypeField}
            style={{ marginBottom: 0 }}
          >
            <div
              role="radiogroup"
              aria-label={AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_TYPE_LABEL}
              className={styles.pillGroup}
            >
              {visibleTriggerModes.map(({ mode, label }) => (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={!isDisplayBanner && triggerMode === mode}
                  disabled={rulesLocked}
                  onClick={() => handleModeSelect(mode)}
                  className={[
                    styles.pill,
                    !isDisplayBanner && triggerMode === mode
                      ? styles.pillSelected
                      : "",
                  ]
                    .join(" ")
                    .trim()}
                >
                  {label}
                </button>
              ))}
              {(displayBannerEnabled || isDisplayBanner) &&
                !displayBannerV2Enabled && (
                <button
                  type="button"
                  role="radio"
                  aria-checked={isDisplayBanner}
                  disabled={rulesLocked}
                  onClick={handleDisplayBannerSelect}
                  className={[
                    styles.pill,
                    isDisplayBanner ? styles.pillSelected : "",
                  ]
                    .join(" ")
                    .trim()}
                >
                  {AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_TYPE_DISPLAY_BANNER}
                </button>
              )}
            </div>
          </Form.Item>

          {!isDisplayBanner && isCampaignStartTrigger && (
            <p className={[styles.stepHint, styles.campaignStartHint].join(" ")}>
              Each user receives this offer or award the next time they open the app after
              the campaign starts, for any audience — including &quot;All users&quot;.
            </p>
          )}
        </>
      )}

      {!isDisplayBanner && (
        <>
          <div className={styles.triggerFollowSection}>
        {triggerMode === "affiliate_referral" && (
          <>
            <Form.Item
              label={AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_AFFILIATE_REFERRAL}
              name="affiliate_id"
              rules={[
                {
                  // Edit hydrate has a code but no affiliate_id (not on
                  // CampaignOut). Require affiliate only when creating or
                  // when the operator clears the code to re-pick.
                  required: !affiliateReferralCode,
                  message: AppStrings.PAM_DYNAMIC_BONUS_AFFILIATE_SELECT_REQUIRED,
                },
              ]}
            >
              <Select
                showSearch
                filterOption={false}
                placeholder={AppStrings.PAM_DYNAMIC_BONUS_AFFILIATE_SEARCH_PLACEHOLDER}
                options={affiliateOptions}
                loading={affiliatesLoading}
                notFoundContent={
                  affiliatesLoading ? <Spin size="small" /> : "No affiliates found"
                }
                onSearch={handleAffiliateSearch}
                onChange={handleAffiliateSelect}
              />
            </Form.Item>

            <Form.Item
              label={AppStrings.PAM_DYNAMIC_BONUS_REFERRAL_CODE_LABEL}
              name="affiliate_referral_code"
              rules={[{ required: true, message: "Select a referral code" }]}
              tooltip="Only active, non-disabled codes are shown"
            >
              <Select
                placeholder={
                  !selectedAffiliateId
                    ? AppStrings.PAM_DYNAMIC_BONUS_SELECT_AFFILIATE_FIRST
                    : promoCodesLoading
                      ? "Loading codes…"
                      : promoCodeOptions.length === 0
                        ? "No active codes"
                        : "Pick a code"
                }
                options={promoCodeOptions}
                loading={promoCodesLoading}
                disabled={!selectedAffiliateId || promoCodesLoading}
                notFoundContent={
                  promoCodesLoading ? <Spin size="small" /> : "No active codes"
                }
                showSearch
                filterOption={filterByLabel}
              />
            </Form.Item>
          </>
        )}

        {isNoneTrigger ? (
          <div className={styles.awardConditionsBox}>
            <Form.Item
              label={AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_RESTRICTIONS}
              style={{ marginBottom: 0 }}
            >
              <ConditionsField
                namePath={["steps", 0, "conditions"]}
                catalog={catalog}
                fields={fieldsForEvent(eventGroups, "")}
                eventKey=""
              />
            </Form.Item>
          </div>
        ) : (
          <Form.List name="steps">
            {(fields, { add, remove }) => (
              <>
                {fields.length === 1 ? (
                  <div
                    className={styles.stepFieldsFlat}
                    data-testid="campaign-wizard-step-flat"
                  >
                    <StepFields
                      field={fields[0]}
                      isFirst
                      form={form}
                      catalog={catalog}
                      eventGroups={eventGroups}
                      eventSelectOptions={eventSelectOptions}
                      stepOneEventKey={stepOneEventKey}
                      usesFixedStepOneTrigger={usesFixedStepOneTrigger}
                      isCampaignStartTrigger={isCampaignStartTrigger}
                      lockConditionsToInjected={
                        triggerMode === "affiliate_referral"
                      }
                      stepsCount={fields.length}
                      isSeries={isSeries}
                      unlockResultOptions={unlockResultOptions}
                      injectedConditions={injectedConditions}
                      percentOfTriggerEnabled={percentOfTriggerEnabled}
                      multiAwardStepsEnabled={multiAwardStepsEnabled}
                      naturalLanguageResultLabels={naturalLanguageResultLabels}
                      cashbackOffersEnabled={cashbackOffersEnabled}
                      creditWalletCashResult={creditWalletCashResult}
                      primaryCurrencyName={primaryCurrencyName}
                      displayBannerV2Enabled={displayBannerV2Enabled}
                      triggerMode={triggerMode}
                      rulesLocked={rulesLocked}
                      readOnly={readOnly}
                      applyResultDefaults={applyResultDefaults}
                      applyDisplayLocationDefaults={applyDisplayLocationDefaults}
                    />
                  </div>
                ) : (
                  <div className={styles.stepsList}>
                    {fields.map((field, index) => {
                      const isFirst = index === 0;
                      const isOpen = !closedStepIndexes.has(index);
                      const panelId = `campaign-wizard-step-panel-${index}`;
                      return (
                        <div key={field.key} className={styles.stepCard}>
                          <div className={styles.stepCardHeader}>
                            <button
                              type="button"
                              className={styles.stepCardToggle}
                              onClick={() => toggleStep(index)}
                              aria-expanded={isOpen}
                              aria-controls={panelId}
                              data-testid={`campaign-wizard-step-toggle-${index}`}
                            >
                              <DownOutlined
                                className={`${styles.stepCardChevron} ${
                                  isOpen ? styles.stepCardChevronOpen : ""
                                }`.trim()}
                              />
                              <span className={styles.stepCardTitle}>
                                {stepDisplayLabel(index)}
                              </span>
                            </button>
                          </div>

                          <Collapse open={isOpen}>
                            <div className={styles.stepCardBody} id={panelId}>
                              <StepFields
                                field={field}
                                isFirst={isFirst}
                                form={form}
                                catalog={catalog}
                                eventGroups={eventGroups}
                                eventSelectOptions={eventSelectOptions}
                                stepOneEventKey={stepOneEventKey}
                                usesFixedStepOneTrigger={usesFixedStepOneTrigger}
                                isCampaignStartTrigger={isCampaignStartTrigger}
                                lockConditionsToInjected={
                                  isFirst && triggerMode === "affiliate_referral"
                                }
                                stepsCount={fields.length}
                                isSeries={isSeries}
                                unlockResultOptions={unlockResultOptions}
                                injectedConditions={injectedConditions}
                                percentOfTriggerEnabled={percentOfTriggerEnabled}
                                multiAwardStepsEnabled={multiAwardStepsEnabled}
                                naturalLanguageResultLabels={naturalLanguageResultLabels}
                                cashbackOffersEnabled={cashbackOffersEnabled}
                                creditWalletCashResult={creditWalletCashResult}
                                primaryCurrencyName={primaryCurrencyName}
                                displayBannerV2Enabled={displayBannerV2Enabled}
                                triggerMode={triggerMode}
                                rulesLocked={rulesLocked}
                                readOnly={readOnly}
                                applyResultDefaults={applyResultDefaults}
                                applyDisplayLocationDefaults={applyDisplayLocationDefaults}
                              />
                            </div>
                          </Collapse>
                          {!isFirst && !lockSeriesStepCount && (
                            <div className={styles.stepCardFooter}>
                              <Button
                                intent="neutral"
                                variant="ghost"
                                iconLeft={<DeleteOutlined />}
                                className={styles.stepTextAction}
                                onClick={() => {
                                  remove(field.name);
                                  setClosedStepIndexes((prev) => {
                                    const next = new Set<number>();
                                    prev.forEach((closedIndex) => {
                                      if (closedIndex === index) return;
                                      next.add(
                                        closedIndex > index
                                          ? closedIndex - 1
                                          : closedIndex,
                                      );
                                    });
                                    return next;
                                  });
                                }}
                                aria-label={`Remove ${stepDisplayLabel(index)}`}
                              >
                                {AppStrings.PAM_DYNAMIC_BONUS_REMOVE_STEP}
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {!lockSeriesStepCount &&
                  !(isDisplayBannerOnSeriesStepOne && watchedSteps.length >= 2) && (
                  <div className={styles.addStepRow}>
                    {isAwardOnCampaignStartStepOne ? (
                      <p className={styles.stepHint}>
                        {AppStrings.PAM_DYNAMIC_BONUS_AWARD_NO_FOLLOW_UP}
                      </p>
                    ) : (
                      <Button
                        intent="neutral"
                        variant="ghost"
                        iconLeft={<PlusOutlined />}
                        className={styles.stepTextAction}
                        onClick={() => {
                          const steps =
                            (form.getFieldValue("steps") as
                              | WizardStepFormValue[]
                              | undefined) ?? [];
                          // Braze Display Info Banner allows an optional
                          // follow-up, not an unbounded series (BONENG-1189).
                          if (isDisplayBannerOnSeriesStepOne && steps.length >= 2) {
                            return;
                          }
                          // campaign_start is step-1-only — a new step must inherit the
                          // shared follow-up event (if one is already picked), never
                          // the step-1 trigger itself.
                          const prefillEventKey = isCampaignStartTrigger
                            ? steps[1]?.event_key
                            : stepOneEventKey;
                          add({ event_key: prefillEventKey || undefined });
                        }}
                      >
                        {AppStrings.PAM_DYNAMIC_BONUS_ADD_STEP}
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </Form.List>
        )}
          </div>

          {showFooter && (
            <div className={styles.stepFooter}>
              <Button intent="neutral" onClick={onPrev}>
                Previous
              </Button>
              <Button intent="primary" onClick={onNext}>
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default TriggerStepsStep;
